import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { X, List } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CreateListModal({ type, onClose, onCreated, preselectedTaskListId = null }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [taskListId, setTaskListId] = useState(preselectedTaskListId || '');
  const [taskLists, setTaskLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingLists, setLoadingLists] = useState(false);

  // Load task lists when creating a reward list
  useEffect(() => {
    if (type === 'reward') {
      loadTaskLists();
    }
  }, [type]);

  const loadTaskLists = async () => {
    setLoadingLists(true);
    try {
      const response = await axios.get(`${API}/task-lists`, { withCredentials: true });
      setTaskLists(response.data);
      // If we have a preselected task list, use it. Otherwise select the first one
      if (preselectedTaskListId) {
        setTaskListId(preselectedTaskListId);
      } else if (response.data.length > 0 && !taskListId) {
        setTaskListId(response.data[0].list_id);
      }
    } catch (error) {
      console.error('Error loading task lists:', error);
    } finally {
      setLoadingLists(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    if (type === 'reward' && !taskListId) {
      toast.error(t('modals.selectTaskList'));
      return;
    }

    setLoading(true);
    try {
      const endpoint = type === 'task' ? `${API}/task-lists` : `${API}/reward-lists`;
      const data = type === 'task' 
        ? { name } 
        : { name, task_list_id: taskListId };
      
      await axios.post(endpoint, data, { withCredentials: true });
      toast.success(t('messages.listCreated'));
      onCreated();
    } catch (error) {
      console.error('Error creating list:', error);
      toast.error(t('messages.errorCreatingList'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50" onClick={onClose}>
      <div
        className="bg-white dark:bg-zinc-800 border-t-2 sm:border-2 border-[#18181B] dark:border-zinc-600 rounded-t-2xl sm:rounded-2xl hard-shadow p-4 sm:p-6 w-full sm:max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="create-list-modal"
      >
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-zinc-900 dark:text-white">
            {type === 'task' ? t('modals.newTaskList') : t('modals.newRewardList')}
          </h2>
          <button
            onClick={onClose}
            className="rounded-full p-1.5 sm:p-2 hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
            data-testid="close-modal-btn"
          >
            <X className="w-5 h-5 text-zinc-900 dark:text-white" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4 sm:mb-6">
            <label className="block text-xs sm:text-sm font-bold mb-1.5 sm:mb-2 text-zinc-900 dark:text-white">{t('modals.listName')}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={type === 'task' ? t('modals.taskListPlaceholder') : t('modals.rewardListPlaceholder')}
              className="w-full h-10 sm:h-12 rounded-lg sm:rounded-xl border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white placeholder-zinc-400 px-3 sm:px-4 text-sm sm:text-base focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(24,24,27,1)] transition-shadow"
              data-testid="list-name-input"
              autoFocus
            />
          </div>

          {/* Task List Selector for Reward Lists */}
          {type === 'reward' && (
            <div className="mb-4 sm:mb-6">
              <label className="block text-xs sm:text-sm font-bold mb-1.5 sm:mb-2 text-zinc-900 dark:text-white">
                <span className="flex items-center gap-1.5">
                  <List className="w-4 h-4" />
                  {t('modals.associatedTaskList')}
                </span>
              </label>
              {loadingLists ? (
                <div className="w-full h-12 rounded-xl border-2 border-zinc-200 dark:border-zinc-600 bg-zinc-50 dark:bg-zinc-700 flex items-center justify-center">
                  <span className="text-sm text-zinc-400">{t('modals.loading')}</span>
                </div>
              ) : taskLists.length === 0 ? (
                <div className="w-full p-4 rounded-xl border-2 border-orange-300 dark:border-orange-600 bg-orange-50 dark:bg-orange-900/30 text-center">
                  <p className="text-sm text-orange-700 dark:text-orange-400">{t('modals.noTaskListsWarning')}</p>
                </div>
              ) : (
                <select
                  value={taskListId}
                  onChange={(e) => setTaskListId(e.target.value)}
                  className="w-full h-10 sm:h-12 rounded-lg sm:rounded-xl border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white px-3 sm:px-4 text-sm sm:text-base focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(24,24,27,1)] transition-shadow"
                  data-testid="task-list-select"
                >
                  {taskLists.map((list) => (
                    <option key={list.list_id} value={list.list_id}>
                      {list.name} ({list.points || 0} pts)
                    </option>
                  ))}
                </select>
              )}
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-2">
                {t('modals.associatedTaskListHelp')}
              </p>
            </div>
          )}

          <div className="flex gap-2 sm:gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-full border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 text-[#18181B] dark:text-white font-bold py-2.5 sm:py-3 px-4 sm:px-6 text-sm sm:text-base hover:bg-zinc-100 dark:hover:bg-zinc-600 hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] active:translate-y-1 active:shadow-none transition-all"
              data-testid="cancel-btn"
            >
              {t('modals.cancel')}
            </button>
            <button
              type="submit"
              disabled={!name.trim() || loading || (type === 'reward' && (!taskListId || taskLists.length === 0))}
              className="flex-1 rounded-full border-2 border-[#18181B] bg-[#18181B] text-white font-bold py-2.5 sm:py-3 px-4 sm:px-6 text-sm sm:text-base hover:bg-[#333] hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] active:translate-y-1 active:shadow-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="create-btn"
            >
              {loading ? t('modals.creating') : t('modals.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
